import { expandSignatureStatuses } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/Documents.utils';
import { NO_SIGNATURE_REQUIRED_FILTER_VALUE } from '@dealstack/frontend/shared/components/Table/MantineTable/Filters/InvestorFilter';
import { DocumentWorkflowInstanceSignatureStatus } from '@dealstack/shared/lib/generated';
import { DateTime } from 'luxon';
import { InvestmentProgrammeDocumentsDataOperations } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/Documents.utils.types';
import {
	emptyToUndefined,
	toIdListFilterValue,
} from '@dealstack/frontend/shared/components/Table/MantineTable/table-utils.ts';

/**
 * The calendar picker hands back JS Dates at *local* midnight, and the backend
 * filters with `gte` on `from` / `lte` on `to`.  Normalise both bounds against
 * the user's local calendar day: `from` becomes the start of the selected start
 * day and `to` becomes the start of the day *after* the selected end day, so the
 * entire end day is included.  This also makes a same-day range (e.g. 12/06 →
 * 12/06) span the full 24h of that day instead of collapsing to an empty/near-
 * empty window.  Returns ISO strings to match the generated DTO types.
 */
export const toInclusiveDateRange = (
	range: { from?: Date; to?: Date } | undefined
): { from?: string; to?: string } | undefined => {
	if (!range) {
		return undefined;
	}
	return {
		from: range.from ? DateTime.fromJSDate(range.from).startOf('day').toISO()! : undefined,
		to: range.to
			? DateTime.fromJSDate(range.to).startOf('day').plus({ days: 1 }).toISO()!
			: undefined,
	};
};

export const transformDocumentFieldFilter = (
	filter: InvestmentProgrammeDocumentsDataOperations['filter']
) => {
	const rawInvestorSignatureStatuses = filter?.investor?.signatureStatuses?.filter(
		(s): s is DocumentWorkflowInstanceSignatureStatus =>
			s !== NO_SIGNATURE_REQUIRED_FILTER_VALUE
	);

	return {
		...filter,
		displayStatuses: filter?.status,
		investorName: filter?.investor?.investorName,
		investorSignatureStatuses: expandSignatureStatuses(rawInvestorSignatureStatuses),
		investorNoSignatureRequired: filter?.investor?.signatureStatuses?.includes(
			NO_SIGNATURE_REQUIRED_FILTER_VALUE
		),
		counterpartyName: filter?.counterparty?.counterpartyName,
		counterpartyRoleNames: filter?.counterparty?.roleNames,
		counterpartySignatureStatuses: expandSignatureStatuses(
			filter?.counterparty?.signatureStatuses
		),
		createdAt: toInclusiveDateRange(filter?.createdAt),
		updatedAt: toInclusiveDateRange(filter?.updatedAt),
		investorId: undefined,
		investorIds: emptyToUndefined(toIdListFilterValue(filter?.investorId)),
		investor: undefined,
		counterparty: undefined,
	};
};
